import React from 'react';
import { 
  Zap, 
  Crown, 
  Lock, 
  HelpCircle, 
  Menu, 
  Calendar, 
  Check, 
  MessageCircle, 
  X, 
  ShieldCheck, 
  Camera, 
  Mic, 
  Calculator, 
  Users, 
  Receipt as ReceiptIcon, 
  ShoppingCart, 
  DollarSign, 
  ChevronDown, 
  ChevronRight, 
  User as UserIcon, 
  LogOut, 
  Compass, 
  Package, 
  TrendingUp, 
  Building2, 
  Boxes, 
  Edit3,
  ClipboardList,
  PenTool,
  Stethoscope,
  Syringe
} from 'lucide-react';
import logo from '../../../assets/logo-rmax.jpeg';
import { StorefrontProfile } from '../types';
import { UserPlanTier } from './PlanUpgradeModal';
import { ActiveView } from '../hooks/useErpPlaygroundState';
import HelpCenterModal from '../../../components/HelpCenterModal';
import ErpSidebarDrawer from './ErpSidebarDrawer';
import FiscalConfigModal from './FiscalConfigModal';
import { AgenticCommandModal } from './AgenticCommandModal';
import InstallPrompt from '../../../components/InstallPrompt';
import PendingDraftBanner from '../../../components/PendingDraftBanner';
import { useIsPlayStore } from '../../../utils/playstore';
import ScreenTourButton from '../../../components/tour/ScreenTourButton';
import FontSizeSelector from '../../../components/FontSizeSelector';
import { getDashboardConfigForNiche } from '../constants/dashboardNicheConfig';

interface ErpHubDashboardProps {
  demoProfile: StorefrontProfile;
  simulatedPlanTier: UserPlanTier;
  calculatedVhbRate: number;
  canAccess: (requiredTier: 'free' | 'pro' | 'premium' | 'enterprise') => boolean;
  handleActionClick: (
    requiredTier: 'free' | 'pro' | 'premium' | 'enterprise',
    action: () => void,
    featureName: string,
    featureDesc?: string
  ) => void;
  onNavigate: (view: ActiveView) => void;
  userNiche?: string;
  userProfession?: string;
  userId?: string;
  quotesCount?: number;
  maxFreeQuotes?: number;
  accessEndsAt?: string | null;
  trialEndsAt?: string | null;
  isTrialActive?: boolean;
  onOpenPaywall?: (plan?: 'monthly' | 'annual' | 'specialist' | 'specialist_annual') => void;
  onLogout?: () => void;
  appointmentsCount?: number;
  weeklyRevenueEstimate?: number;
  userName?: string;
}

export default function ErpHubDashboard({
  demoProfile,
  simulatedPlanTier,
  calculatedVhbRate,
  canAccess,
  handleActionClick,
  onNavigate,
  userNiche,
  userProfession,
  userId,
  quotesCount = 0,
  maxFreeQuotes = 5,
  accessEndsAt,
  trialEndsAt,
  isTrialActive = false,
  onOpenPaywall,
  onLogout,
  appointmentsCount = 0,
  weeklyRevenueEstimate = 0,
  userName
}: ErpHubDashboardProps) {
  const isPlayStore = useIsPlayStore();
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = React.useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = React.useState(false);
  const [isFiscalModalOpen, setIsFiscalModalOpen] = React.useState(false);
  const [isAgenticModalOpen, setIsAgenticModalOpen] = React.useState(false);
  const [agenticOpenCamera, setAgenticOpenCamera] = React.useState(false);
  const [agenticOpenVoice, setAgenticOpenVoice] = React.useState(false);
  const [isAccountModalOpen, setIsAccountModalOpen] = React.useState(false);
  const [isMoreOptionsOpen, setIsMoreOptionsOpen] = React.useState(false);

  // Atalho universal de teclado: Ctrl + K ou Cmd + K
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setAgenticOpenCamera(false);
        setAgenticOpenVoice(false);
        setIsAgenticModalOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const getInitials = (name: string) => {
    if (!name) return 'RM';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const clusterConfig = React.useMemo(() => {
    return getDashboardConfigForNiche(userNiche, userProfession);
  }, [userNiche, userProfession]);

  const effectiveNiche = userNiche || demoProfile.nicheBadges?.[0] || clusterConfig.badgeLabel || demoProfile.tagline || 'Atendimento Profissional';
  const ownerFirstName = (userName || demoProfile.displayName || 'Empreendedor').trim().split(' ')[0];

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  const greeting = getGreeting();
  const isSpecialistOrEnterprise = simulatedPlanTier === 'premium' || simulatedPlanTier === 'enterprise' || isTrialActive;
  const isSpecialistUnlocked = isSpecialistOrEnterprise;
  const isEnterpriseUnlocked = simulatedPlanTier === 'enterprise' || isTrialActive;

  const openAgenticDefault = () => {
    setAgenticOpenCamera(false);
    setAgenticOpenVoice(false);
    setIsAgenticModalOpen(true);
  };

  const openAgenticWithCamera = () => {
    setAgenticOpenVoice(false);
    setAgenticOpenCamera(true);
    setIsAgenticModalOpen(true);
  };

  const openAgenticWithVoice = () => {
    setAgenticOpenCamera(false);
    setAgenticOpenVoice(true);
    setIsAgenticModalOpen(true);
  };

  // Formatação amigável da data de vencimento / expiração do plano
  const formattedExpiry = React.useMemo(() => {
    const rawDate = isTrialActive ? trialEndsAt : accessEndsAt;
    if (!rawDate) return null;
    try {
      const d = new Date(rawDate);
      if (isNaN(d.getTime())) return null;
      const day = d.getDate();
      const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
      const month = months[d.getMonth()];
      const year = d.getFullYear();
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const target = new Date(d);
      target.setHours(0, 0, 0, 0);
      const diffTime = target.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return {
        formatted: `${day} de ${month} de ${year}`,
        daysRemaining: diffDays
      };
    } catch {
      return null;
    }
  }, [accessEndsAt, trialEndsAt, isTrialActive]);

  return (
    <div className="w-full min-h-screen bg-zinc-50 flex flex-col font-sans pb-16">
      {/* ───────────────────────────────────────────────────────────────────────── */}
      {/* 1. APP BAR SUPERIOR SLIM (56px) - Padrão Material 3 Sóbrio               */}
      {/* ───────────────────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-zinc-200/80 px-4 sm:px-6 h-14 flex items-center shadow-2xs w-full">
        <div className="max-w-2xl mx-auto w-full flex items-center justify-between">
          
          {/* Lado Esquerdo: Hambúrguer + Logo R-MAX */}
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 -ml-1.5 rounded-xl text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100 active:scale-95 transition-all cursor-pointer"
              title="Abrir menu de navegação"
              aria-label="Abrir menu lateral"
            >
              <Menu className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-2">
              <img 
                src={logo} 
                alt="R-MAX" 
                className="h-7 w-auto rounded-lg object-contain shadow-2xs"
              />
              <span className="text-xs font-black text-zinc-900 tracking-tight leading-none">
                R-MAX
              </span>
            </div>
          </div>

          {/* Lado Direito: Ajuda + Minha Conta + Badge do Plano */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsHelpModalOpen(true)}
              className="p-2 rounded-xl text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors cursor-pointer"
              title="Ajuda e Passo a Passo"
              aria-label="Abrir central de ajuda"
            >
              <HelpCircle className="h-4.5 w-4.5" />
            </button>

            <button
              type="button"
              onClick={() => setIsAccountModalOpen(true)}
              className="p-2 rounded-xl text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100 transition-colors cursor-pointer flex items-center gap-1.5"
              title="Minha Conta e Configurações"
              aria-label="Abrir minha conta"
            >
              <div className="w-6 h-6 rounded-lg bg-zinc-200 text-zinc-800 flex items-center justify-center font-black text-[10px]">
                {getInitials(demoProfile.displayName || ownerFirstName)}
              </div>
            </button>

            <button
              type="button"
              onClick={() => setIsSubscriptionModalOpen(true)}
              className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full flex items-center gap-1 shadow-2xs cursor-pointer transition-all active:scale-95 ${
                simulatedPlanTier === 'enterprise'
                  ? 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100'
                  : simulatedPlanTier === 'premium'
                  ? 'bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100'
                  : simulatedPlanTier === 'pro'
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                  : 'bg-zinc-100 text-zinc-600 border border-zinc-200 hover:bg-zinc-200'
              }`}
              title="Clique para ver detalhes do seu plano"
            >
              {simulatedPlanTier === 'enterprise' && <ShieldCheck className="h-3 w-3 text-indigo-600" />}
              {simulatedPlanTier === 'premium' && <Crown className="h-3 w-3 text-amber-600" />}
              {simulatedPlanTier === 'pro' && <Zap className="h-3 w-3 text-emerald-600" />}
              <span>{simulatedPlanTier === 'enterprise' ? 'Empresa' : simulatedPlanTier === 'premium' ? 'Especialista' : simulatedPlanTier === 'pro' ? 'PRO' : 'Grátis'}</span>
            </button>
          </div>

        </div>
      </header>

      {/* ───────────────────────────────────────────────────────────────────────── */}
      {/* CORPO PRINCIPAL DESCOMPLICADO                                             */}
      {/* ───────────────────────────────────────────────────────────────────────── */}
      <main className="max-w-2xl mx-auto w-full px-4 sm:px-6 pt-3 pb-24 space-y-3.5 flex-1">

        {/* 2. CABEÇALHO ACOLHEDOR COM NOME DA EMPRESA E DO USUÁRIO */}
        <div className="flex items-center justify-between pt-1">
          <div>
            <span className="text-[11px] font-bold text-zinc-400 block uppercase tracking-wider">
              {demoProfile.displayName || 'Minha Empresa'}
            </span>
            <h1 className="text-xl sm:text-2xl font-black text-zinc-900 tracking-tight leading-tight">
              {greeting}, {ownerFirstName}
            </h1>
          </div>
          <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
            {isTrialActive ? 'Teste 14 Dias' : 'Sistema Ativo'}
          </span>
        </div>

        {/* 3. A LINHA DE COMANDO NO TOPO (DESTAQUE NOBRE — "No que posso te ajudar?") */}
        <div className="bg-white border border-zinc-200/90 rounded-2xl p-2.5 sm:p-3 shadow-2xs hover:border-amber-400/80 transition-all">
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={openAgenticDefault}
              className="w-10 h-10 rounded-xl bg-amber-500/15 text-amber-800 flex items-center justify-center shrink-0 border border-amber-500/25 hover:scale-105 active:scale-95 transition-transform cursor-pointer"
              title="Abrir Assistente R-MAX"
            >
              <Zap className="h-5 w-5 fill-current" />
            </button>

            <button
              type="button"
              onClick={openAgenticDefault}
              className="flex-1 text-left py-1 cursor-pointer group min-w-0"
            >
              <span className="text-xs sm:text-sm font-bold text-zinc-800 group-hover:text-amber-800 block truncate">
                No que posso te ajudar hoje?
              </span>
              <span className="text-[11px] text-zinc-400 font-medium block truncate">
                Toque para falar, digitar comando ou tirar foto da nota
              </span>
            </button>

            {/* Ações Rápidas: Câmera (📷) e Microfone (🎙️) */}
            <div className="flex items-center gap-1 shrink-0 border-l border-zinc-100 pl-2">
              <button
                type="button"
                onClick={openAgenticWithCamera}
                className="p-2 rounded-xl text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 active:scale-90 transition-all cursor-pointer"
                title="Tirar foto de cupom ou nota fiscal"
                aria-label="Fotografar nota fiscal"
              >
                <Camera className="h-4.5 w-4.5" />
              </button>
              <button
                type="button"
                onClick={openAgenticWithVoice}
                className="p-2 rounded-xl text-zinc-500 hover:text-amber-700 hover:bg-amber-50 active:scale-90 transition-all cursor-pointer"
                title="Falar comando por áudio"
                aria-label="Comando por voz"
              >
                <Mic className="h-4.5 w-4.5" />
              </button>
            </div>
          </div>
        </div>

        {/* 3.1 BANNER DE ORÇAMENTO EM EDIÇÃO (Efeito Zeigarnik) */}
        <PendingDraftBanner 
          userId={userId} 
          onResume={() => onNavigate('orcamento')} 
        />

        {/* 4. CARD "SEU DIA" (Resumo Imediato de Valor para o Empreendedor) */}
        <div className="bg-white border border-zinc-200/80 rounded-2xl p-4 shadow-2xs">
          <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-2">
            Seu Dia
          </span>

          {isSpecialistOrEnterprise ? (
            <div className="grid grid-cols-2 gap-3 divide-x divide-zinc-100">
              <div className="space-y-0.5">
                <span className="text-xl sm:text-2xl font-black text-zinc-900 leading-none block">
                  {appointmentsCount}
                </span>
                <p className="text-[11px] sm:text-xs text-zinc-500 font-medium">
                  {appointmentsCount === 0
                    ? 'nenhum agendamento hoje'
                    : appointmentsCount === 1
                    ? '1 agendamento hoje'
                    : `${appointmentsCount} compromissos hoje`}
                </p>
              </div>
              <div className="space-y-0.5 pl-3">
                <span className="text-xl sm:text-2xl font-black text-emerald-600 leading-none block">
                  R$ {weeklyRevenueEstimate > 0 ? weeklyRevenueEstimate.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}
                </span>
                <p className="text-[11px] sm:text-xs text-zinc-500 font-medium">
                  {weeklyRevenueEstimate > 0 ? 'a receber esta semana' : 'nenhum recebimento pendente'}
                </p>
              </div>
            </div>
          ) : simulatedPlanTier === 'pro' ? (
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-black text-zinc-900 block">
                  Orçamentos Ilimitados Ativos
                </span>
                <p className="text-xs text-zinc-500 font-medium">
                  {quotesCount} {quotesCount === 1 ? 'orçamento emitido' : 'orçamentos emitidos'} este mês
                </p>
              </div>
              <span className="text-[11px] font-black text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
                Plano PRO
              </span>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs font-bold text-zinc-700">
                <span>{Math.max(0, maxFreeQuotes - quotesCount)} orçamentos restantes</span>
                <span className="text-zinc-400">de {maxFreeQuotes} grátis este mês</span>
              </div>
              <div className="w-full h-2 rounded-full bg-zinc-100 overflow-hidden">
                <div
                  className="h-full bg-amber-500 rounded-full transition-all"
                  style={{ width: `${Math.min(100, (quotesCount / maxFreeQuotes) * 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* 5. SEÇÃO "O QUE VOCÊ QUER FAZER?" (Os Pilares do Trabalho Diário) */}
        <div className="space-y-2.5">
          <span className="text-xs font-black uppercase tracking-wider text-zinc-400 px-1">
            O que você quer fazer?
          </span>

          {/* Botão 1: Fazer um Orçamento (Destaque Principal) */}
          <button
            type="button"
            onClick={() => onNavigate('orcamento')}
            className="w-full p-3.5 bg-white hover:bg-zinc-50 border border-zinc-200/90 rounded-2xl flex items-center justify-between shadow-2xs hover:border-amber-400/80 transition-all cursor-pointer group active:scale-[0.99]"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-zinc-100 text-zinc-700 group-hover:bg-amber-50 group-hover:text-amber-800 border border-zinc-200/80 flex items-center justify-center shrink-0 transition-colors">
                <Calculator className="h-5 w-5" />
              </div>
              <div className="text-left">
                <h3 className="text-xs sm:text-sm font-black text-zinc-900 group-hover:text-amber-900">
                  Fazer um orçamento
                </h3>
                <p className="text-[11px] text-zinc-400 font-medium">
                  Calcular preço de venda com lucro garantido
                </p>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-zinc-300 group-hover:text-amber-500 transition-colors" />
          </button>

          {/* Grade 2x2 com as ações diárias do plano */}
          <div className="grid grid-cols-2 gap-2.5">
            {isSpecialistOrEnterprise ? (
              clusterConfig.cluster === 'campo_obras' ? (
                <>
                  {/* Técnico / Obras 1: Nova Ordem de Serviço */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('premium', () => onNavigate('os'), 'Ordens de Serviço com Fotos', 'Vistorias com fotos antes, durante e depois.')}
                    className="p-3 bg-white hover:bg-zinc-50 border border-zinc-200/90 rounded-2xl flex items-center gap-3 shadow-2xs hover:border-zinc-300 transition-all cursor-pointer group text-left active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-zinc-100 text-zinc-700 border border-zinc-200/80 flex items-center justify-center shrink-0">
                      <ClipboardList className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-zinc-900 truncate">Nova Ordem de Serviço</h4>
                      <p className="text-[10px] text-zinc-400 truncate">Vistoria com fotos</p>
                    </div>
                  </button>

                  {/* Técnico / Obras 2: Cadastrar Cliente */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('premium', () => onNavigate('clientes'), 'Meus Clientes', 'Telefones e endereço.')}
                    className="p-3 bg-white hover:bg-zinc-50 border border-zinc-200/90 rounded-2xl flex items-center gap-3 shadow-2xs hover:border-zinc-300 transition-all cursor-pointer group text-left active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-zinc-100 text-zinc-700 border border-zinc-200/80 flex items-center justify-center shrink-0">
                      <Users className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-zinc-900 truncate">Cadastrar cliente</h4>
                      <p className="text-[10px] text-zinc-400 truncate">Telefones e endereço</p>
                    </div>
                  </button>

                  {/* Técnico / Obras 3: Emitir Recibo PIX */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('premium', () => onNavigate('recibo'), 'Recibos PIX', 'Comprovante com chave PIX.')}
                    className="p-3 bg-white hover:bg-emerald-50/40 border border-zinc-200/90 rounded-2xl flex items-center gap-3 shadow-2xs hover:border-emerald-300 transition-all cursor-pointer group text-left active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center shrink-0">
                      <ReceiptIcon className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-zinc-900 group-hover:text-emerald-900 truncate">Emitir recibo</h4>
                      <p className="text-[10px] text-zinc-400 truncate">Comprovante com PIX</p>
                    </div>
                  </button>

                  {/* Técnico / Obras 4: Colher Assinatura */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('premium', () => onNavigate('assinatura'), 'Assinatura do Cliente', 'Colha a assinatura direto na tela do celular.')}
                    className="p-3 bg-white hover:bg-zinc-50 border border-zinc-200/90 rounded-2xl flex items-center gap-3 shadow-2xs hover:border-zinc-300 transition-all cursor-pointer group text-left active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-zinc-100 text-zinc-700 border border-zinc-200/80 flex items-center justify-center shrink-0">
                      <PenTool className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-zinc-900 truncate">Colher assinatura</h4>
                      <p className="text-[10px] text-zinc-400 truncate">Assinatura na tela</p>
                    </div>
                  </button>
                </>
              ) : (
                <>
                  {/* Geral / Salão / Outros 1: Ver Agenda de Hoje */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('premium', () => onNavigate('agenda'), 'Agenda & Atendimentos', 'Horários e atendimentos.')}
                    className="p-3 bg-white hover:bg-zinc-50 border border-zinc-200/90 rounded-2xl flex items-center gap-3 shadow-2xs hover:border-zinc-300 transition-all cursor-pointer group text-left active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-zinc-100 text-zinc-700 border border-zinc-200/80 flex items-center justify-center shrink-0">
                      <Calendar className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-zinc-900 truncate">Ver agenda de hoje</h4>
                      <p className="text-[10px] text-zinc-400 truncate">Horários e encomendas</p>
                    </div>
                  </button>

                  {/* Geral / Salão / Outros 2: Cadastrar Cliente */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('premium', () => onNavigate('clientes'), 'Meus Clientes', 'Telefones e endereço.')}
                    className="p-3 bg-white hover:bg-zinc-50 border border-zinc-200/90 rounded-2xl flex items-center gap-3 shadow-2xs hover:border-zinc-300 transition-all cursor-pointer group text-left active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-zinc-100 text-zinc-700 border border-zinc-200/80 flex items-center justify-center shrink-0">
                      <Users className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-zinc-900 truncate">Cadastrar cliente</h4>
                      <p className="text-[10px] text-zinc-400 truncate">Telefones e endereço</p>
                    </div>
                  </button>

                  {/* Geral / Salão / Outros 3: Emitir Recibo */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('premium', () => onNavigate('recibo'), 'Recibos PIX', 'Comprovante com chave PIX.')}
                    className="p-3 bg-white hover:bg-emerald-50/40 border border-zinc-200/90 rounded-2xl flex items-center gap-3 shadow-2xs hover:border-emerald-300 transition-all cursor-pointer group text-left active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center shrink-0">
                      <ReceiptIcon className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-zinc-900 group-hover:text-emerald-900 truncate">Emitir recibo</h4>
                      <p className="text-[10px] text-zinc-400 truncate">Comprovante com PIX</p>
                    </div>
                  </button>

                  {/* Geral / Salão / Outros 4: Anotar uma Compra */}
                  <button
                    type="button"
                    onClick={() => onNavigate('custos')}
                    className="p-3 bg-white hover:bg-zinc-50 border border-zinc-200/90 rounded-2xl flex items-center gap-3 shadow-2xs hover:border-zinc-300 transition-all cursor-pointer group text-left active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-zinc-100 text-zinc-700 border border-zinc-200/80 flex items-center justify-center shrink-0">
                      <ShoppingCart className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-zinc-900 truncate">Anotar uma compra</h4>
                      <p className="text-[10px] text-zinc-400 truncate">Materiais e insumos</p>
                    </div>
                  </button>
                </>
              )
            ) : simulatedPlanTier === 'pro' ? (
              <>
                {/* Pro: Produtos e Receitas */}
                <button
                  type="button"
                  onClick={() => onNavigate('custos')}
                  className="p-3 bg-white hover:bg-zinc-50 border border-zinc-200/90 rounded-2xl flex items-center gap-3 shadow-2xs hover:border-zinc-300 transition-all cursor-pointer group text-left active:scale-[0.99]"
                >
                  <div className="w-9 h-9 rounded-xl bg-zinc-100 text-zinc-700 border border-zinc-200/80 flex items-center justify-center shrink-0">
                    <Package className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-zinc-900 truncate">Meus produtos</h4>
                    <p className="text-[10px] text-zinc-400 truncate">Receitas e custo base</p>
                  </div>
                </button>

                {/* Pro: Ingredientes e Materiais */}
                <button
                  type="button"
                  onClick={() => onNavigate('custos')}
                  className="p-3 bg-white hover:bg-zinc-50 border border-zinc-200/90 rounded-2xl flex items-center gap-3 shadow-2xs hover:border-zinc-300 transition-all cursor-pointer group text-left active:scale-[0.99]"
                >
                  <div className="w-9 h-9 rounded-xl bg-zinc-100 text-zinc-700 border border-zinc-200/80 flex items-center justify-center shrink-0">
                    <ShoppingCart className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-zinc-900 truncate">Anotar compra</h4>
                    <p className="text-[10px] text-zinc-400 truncate">Custos de insumos</p>
                  </div>
                </button>

                {/* Pro: Histórico de Orçamentos */}
                <button
                  type="button"
                  onClick={() => onNavigate('cockpit')}
                  className="p-3 bg-white hover:bg-zinc-50 border border-zinc-200/90 rounded-2xl flex items-center gap-3 shadow-2xs hover:border-zinc-300 transition-all cursor-pointer group text-left active:scale-[0.99]"
                >
                  <div className="w-9 h-9 rounded-xl bg-zinc-100 text-zinc-700 border border-zinc-200/80 flex items-center justify-center shrink-0">
                    <Boxes className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-zinc-900 truncate">Ver histórico</h4>
                    <p className="text-[10px] text-zinc-400 truncate">Propostas emitidas</p>
                  </div>
                </button>

                {/* Pro: Clientes (Upgrade) */}
                <button
                  type="button"
                  onClick={() => handleActionClick('premium', () => onNavigate('clientes'), 'Meus Clientes', 'Controle completo de clientes e histórico com WhatsApp.')}
                  className="p-3 bg-zinc-100/70 border border-zinc-200/80 rounded-2xl flex items-center gap-3 transition-all cursor-pointer text-left active:scale-[0.99]"
                >
                  <div className="w-9 h-9 rounded-xl bg-zinc-200 text-zinc-500 border border-zinc-300/80 flex items-center justify-center shrink-0">
                    <Lock className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-zinc-700 truncate">Agenda e Clientes</h4>
                    <p className="text-[10px] text-zinc-400 truncate">Plano Especialista</p>
                  </div>
                </button>
              </>
            ) : (
              <>
                {/* Grátis: Insumos Básicos */}
                <button
                  type="button"
                  onClick={() => onNavigate('custos')}
                  className="p-3 bg-white hover:bg-zinc-50 border border-zinc-200/90 rounded-2xl flex items-center gap-3 shadow-2xs hover:border-zinc-300 transition-all cursor-pointer group text-left active:scale-[0.99]"
                >
                  <div className="w-9 h-9 rounded-xl bg-zinc-100 text-zinc-700 border border-zinc-200/80 flex items-center justify-center shrink-0">
                    <ShoppingCart className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-zinc-900 truncate">Anotar compra</h4>
                    <p className="text-[10px] text-zinc-400 truncate">Materiais e insumos</p>
                  </div>
                </button>

                {/* Grátis: Como Funciona */}
                <button
                  type="button"
                  onClick={() => setIsHelpModalOpen(true)}
                  className="p-3 bg-white hover:bg-zinc-50 border border-zinc-200/90 rounded-2xl flex items-center gap-3 shadow-2xs hover:border-zinc-300 transition-all cursor-pointer group text-left active:scale-[0.99]"
                >
                  <div className="w-9 h-9 rounded-xl bg-zinc-100 text-zinc-700 border border-zinc-200/80 flex items-center justify-center shrink-0">
                    <Compass className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-zinc-900 truncate">Como funciona</h4>
                    <p className="text-[10px] text-zinc-400 truncate">Rever passo a passo</p>
                  </div>
                </button>

                {/* Grátis: Dados da Empresa */}
                <button
                  type="button"
                  onClick={() => setIsAccountModalOpen(true)}
                  className="p-3 bg-white hover:bg-zinc-50 border border-zinc-200/90 rounded-2xl flex items-center gap-3 shadow-2xs hover:border-zinc-300 transition-all cursor-pointer group text-left active:scale-[0.99]"
                >
                  <div className="w-9 h-9 rounded-xl bg-zinc-100 text-zinc-700 border border-zinc-200/80 flex items-center justify-center shrink-0">
                    <UserIcon className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-zinc-900 truncate">Minha empresa</h4>
                    <p className="text-[10px] text-zinc-400 truncate">Dados e logotipo</p>
                  </div>
                </button>

                {/* Grátis: Conhecer Especialista */}
                <button
                  type="button"
                  onClick={() => onOpenPaywall?.('annual')}
                  className="p-3 bg-amber-50/50 hover:bg-amber-50 border border-amber-200 rounded-2xl flex items-center gap-3 transition-all cursor-pointer text-left active:scale-[0.99]"
                >
                  <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-800 border border-amber-300/80 flex items-center justify-center shrink-0">
                    <Crown className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-amber-950 truncate">Liberar tudo</h4>
                    <p className="text-[10px] text-amber-700 truncate">Ver planos R-MAX</p>
                  </div>
                </button>
              </>
            )}
          </div>

          {/* Botão Largo: Ver Quanto Ganhei no Mês */}
          <button
            type="button"
            onClick={() => onNavigate('cockpit')}
            className="w-full p-3.5 bg-white hover:bg-zinc-50 border border-zinc-200/90 rounded-2xl flex items-center justify-between shadow-2xs hover:border-zinc-300 transition-all cursor-pointer group active:scale-[0.99]"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-zinc-100 text-zinc-700 group-hover:bg-emerald-50 group-hover:text-emerald-800 border border-zinc-200/80 flex items-center justify-center shrink-0 transition-colors">
                <DollarSign className="h-5 w-5" />
              </div>
              <div className="text-left">
                <h3 className="text-xs sm:text-sm font-black text-zinc-900">
                  Ver quanto ganhei no mês
                </h3>
                <p className="text-[11px] text-zinc-400 font-medium">
                  Vendas, gastos fixos e sobra de lucro limpo
                </p>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-zinc-300 group-hover:text-zinc-600 transition-colors" />
          </button>
        </div>

        {/* 6. SEÇÃO "MAIS OPÇÕES" (A GAVETA ORGANIZADA EM 3 BLOCOS HUMANOS) */}
        <div className="pt-1">
          <button
            type="button"
            onClick={() => setIsMoreOptionsOpen((prev) => !prev)}
            className="w-full py-2.5 px-4 rounded-xl text-xs font-bold text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 flex items-center justify-center gap-1.5 transition-all cursor-pointer"
          >
            <span>{isMoreOptionsOpen ? 'Recolher opções' : 'Mais opções'}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${isMoreOptionsOpen ? 'rotate-180' : ''}`} />
          </button>

          {isMoreOptionsOpen && (
            <div className="space-y-4 pt-2 animate-in fade-in duration-200">
              
              {/* Bloco 1: Seu Negócio */}
              <div className="space-y-1.5">
                <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 px-1">
                  Seu Negócio
                </span>
                <div className="bg-white border border-zinc-200/80 rounded-2xl divide-y divide-zinc-100 overflow-hidden shadow-2xs">
                  {/* Meus Produtos e Preços */}
                  <button
                    type="button"
                    onClick={() => onNavigate('custos')}
                    className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 text-left transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5">
                      <Package className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-900" />
                      <div>
                        <h4 className="text-xs font-bold text-zinc-900">Meus produtos e preços</h4>
                        <p className="text-[10px] text-zinc-400">Receitas e custo base</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-300" />
                  </button>

                  {/* Ingredientes e Materiais */}
                  <button
                    type="button"
                    onClick={() => onNavigate('custos')}
                    className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 text-left transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5">
                      <Boxes className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-900" />
                      <div>
                        <h4 className="text-xs font-bold text-zinc-900">Ingredientes e materiais</h4>
                        <p className="text-[10px] text-zinc-400">Insumos guardados para uso</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-300" />
                  </button>

                  {/* Ordens de Serviço & Fotos */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('premium', () => onNavigate('os'), 'Ordens de Serviço com Fotos', 'Vistorias com fotos antes, durante e depois.')}
                    className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 text-left transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5">
                      <ClipboardList className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-900" />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h4 className="text-xs font-bold text-zinc-900">Ordens de serviço com fotos</h4>
                          {!isSpecialistUnlocked && (
                            <span className="text-[9px] font-black text-amber-700 bg-amber-50 border border-amber-200/80 px-1.5 py-0.2 rounded">Especialista</span>
                          )}
                        </div>
                        <p className="text-[10px] text-zinc-400">Vistorias com laudo fotográfico</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-300" />
                  </button>

                  {/* Assinatura do Cliente */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('premium', () => onNavigate('assinatura'), 'Assinatura do Cliente', 'Colha a assinatura na tela do celular.')}
                    className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 text-left transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5">
                      <PenTool className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-900" />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h4 className="text-xs font-bold text-zinc-900">Assinatura do cliente na tela</h4>
                          {!isSpecialistUnlocked && (
                            <span className="text-[9px] font-black text-amber-700 bg-amber-50 border border-amber-200/80 px-1.5 py-0.2 rounded">Especialista</span>
                          )}
                        </div>
                        <p className="text-[10px] text-zinc-400">Aceite digital direto no celular</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-300" />
                  </button>

                  {/* Fornecedores */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('premium', () => onNavigate('fornecedores'), 'Gestão de Fornecedores', 'Busca CNPJ na Receita Federal e notas.')}
                    className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 text-left transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5">
                      <Building2 className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-900" />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h4 className="text-xs font-bold text-zinc-900">Fornecedores</h4>
                          {!isSpecialistUnlocked && (
                            <span className="text-[9px] font-black text-amber-700 bg-amber-50 border border-amber-200/80 px-1.5 py-0.2 rounded">Especialista</span>
                          )}
                        </div>
                        <p className="text-[10px] text-zinc-400">De quem eu compro e notas</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-300" />
                  </button>
                </div>
              </div>

              {/* Bloco 2: Meu Dinheiro */}
              <div className="space-y-1.5">
                <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 px-1">
                  Meu Dinheiro
                </span>
                <div className="bg-white border border-zinc-200/80 rounded-2xl divide-y divide-zinc-100 overflow-hidden shadow-2xs">
                  {/* Quanto ganhei no mês */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('premium', () => onNavigate('cockpit'), 'Painel Financeiro', 'Vendas, gastos fixos e sobra de lucro limpo.')}
                    className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 text-left transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5">
                      <DollarSign className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-900" />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h4 className="text-xs font-bold text-zinc-900">Quanto ganhei no mês</h4>
                          {!isSpecialistUnlocked && (
                            <span className="text-[9px] font-black text-amber-700 bg-amber-50 border border-amber-200/80 px-1.5 py-0.2 rounded">Especialista</span>
                          )}
                        </div>
                        <p className="text-[10px] text-zinc-400">Vendas, gastos e lucro real</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-300" />
                  </button>

                  {/* Recibos de PIX */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('premium', () => onNavigate('recibo'), 'Recibos PIX', 'Comprovante com QR Code Copia e Cola.')}
                    className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 text-left transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5">
                      <ReceiptIcon className="h-4.5 w-4.5 text-emerald-600" />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h4 className="text-xs font-bold text-zinc-900">Recibos de PIX</h4>
                          {!isSpecialistUnlocked && (
                            <span className="text-[9px] font-black text-amber-700 bg-amber-50 border border-amber-200/80 px-1.5 py-0.2 rounded">Especialista</span>
                          )}
                        </div>
                        <p className="text-[10px] text-zinc-400">Comprovante com QR Code</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-300" />
                  </button>

                  {/* Fluxo de Vendas */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('premium', () => onNavigate('gestao'), 'Fluxo de Vendas', 'Acompanhe propostas aprovadas e faturamento.')}
                    className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 text-left transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5">
                      <TrendingUp className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-900" />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h4 className="text-xs font-bold text-zinc-900">Fluxo de vendas e propostas</h4>
                          {!isSpecialistUnlocked && (
                            <span className="text-[9px] font-black text-amber-700 bg-amber-50 border border-amber-200/80 px-1.5 py-0.2 rounded">Especialista</span>
                          )}
                        </div>
                        <p className="text-[10px] text-zinc-400">Propostas aprovadas e faturamento</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-300" />
                  </button>

                  {/* Preços da minha região */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('premium', () => onNavigate('radar'), 'Radar de Mercado', 'Média de preços cobrados na sua cidade.')}
                    className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 text-left transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5">
                      <TrendingUp className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-900" />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h4 className="text-xs font-bold text-zinc-900">Preços da minha região</h4>
                          {!isSpecialistUnlocked && (
                            <span className="text-[9px] font-black text-amber-700 bg-amber-50 border border-amber-200/80 px-1.5 py-0.2 rounded">Especialista</span>
                          )}
                        </div>
                        <p className="text-[10px] text-zinc-400">Quanto outros cobram na cidade</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-300" />
                  </button>
                </div>
              </div>

              {/* Bloco 3: Para Crescer & Empresa */}
              <div className="space-y-1.5">
                <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 px-1">
                  Para Crescer & Empresa
                </span>
                <div className="bg-white border border-zinc-200/80 rounded-2xl divide-y divide-zinc-100 overflow-hidden shadow-2xs">
                  {/* Minha Equipe */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('premium', () => onNavigate('equipe'), 'Minha Equipe', 'Adicione colaboradores com permissões.')}
                    className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 text-left transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5">
                      <Users className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-900" />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h4 className="text-xs font-bold text-zinc-900">Minha equipe</h4>
                          {!isSpecialistUnlocked && (
                            <span className="text-[9px] font-black text-amber-700 bg-amber-50 border border-amber-200/80 px-1.5 py-0.2 rounded">Especialista</span>
                          )}
                        </div>
                        <p className="text-[10px] text-zinc-400">Quem trabalha comigo e permissões</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-300" />
                  </button>

                  {/* Estoque & Lotes */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('enterprise', () => onNavigate('estoque_hub'), 'Estoque & Lotes', 'Balancete financeiro, validade e extrato analítico.')}
                    className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 text-left transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5">
                      <Boxes className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-900" />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h4 className="text-xs font-bold text-zinc-900">Estoque & controle de lotes</h4>
                          {!isEnterpriseUnlocked && (
                            <span className="text-[9px] font-black text-indigo-700 bg-indigo-50 border border-indigo-200/80 px-1.5 py-0.2 rounded">Empresa</span>
                          )}
                        </div>
                        <p className="text-[10px] text-zinc-400">Balancete financeiro e validade</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-300" />
                  </button>

                  {/* Portal do Contador */}
                  <button
                    type="button"
                    onClick={() => handleActionClick('enterprise', () => onNavigate('portal_contador' as any), 'Portal do Contador', 'Fechamento fiscal e exportação contábil.')}
                    className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 text-left transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5">
                      <Building2 className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-900" />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h4 className="text-xs font-bold text-zinc-900">Portal do contador</h4>
                          {!isEnterpriseUnlocked && (
                            <span className="text-[9px] font-black text-indigo-700 bg-indigo-50 border border-indigo-200/80 px-1.5 py-0.2 rounded">Empresa</span>
                          )}
                        </div>
                        <p className="text-[10px] text-zinc-400">Fechamento fiscal e exportação contábil</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-300" />
                  </button>

                  {/* Saúde / Veterinária Específicos */}
                  {clusterConfig.cluster === 'saude_clinica' && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleActionClick('enterprise', () => onNavigate('prontuario_soap' as any), 'Prontuário SOAP Legal', 'Anamnese e histórico médico imutável.')}
                        className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 text-left transition-colors cursor-pointer group"
                      >
                        <div className="flex items-center gap-2.5">
                          <Stethoscope className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-900" />
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-xs font-bold text-zinc-900">Prontuário SOAP & CRM</h4>
                              {!isEnterpriseUnlocked && (
                                <span className="text-[9px] font-black text-indigo-700 bg-indigo-50 border border-indigo-200/80 px-1.5 py-0.2 rounded">Empresa</span>
                              )}
                            </div>
                            <p className="text-[10px] text-zinc-400">Anamnese e histórico legal</p>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-zinc-300" />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleActionClick('enterprise', () => onNavigate('retorno_vacinas' as any), 'Central de Vacinas no WhatsApp', 'Lembretes automáticos para tutores.')}
                        className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 text-left transition-colors cursor-pointer group"
                      >
                        <div className="flex items-center gap-2.5">
                          <Syringe className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-900" />
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-xs font-bold text-zinc-900">Central de retorno de vacinas</h4>
                              {!isEnterpriseUnlocked && (
                                <span className="text-[9px] font-black text-indigo-700 bg-indigo-50 border border-indigo-200/80 px-1.5 py-0.2 rounded">Empresa</span>
                              )}
                            </div>
                            <p className="text-[10px] text-zinc-400">Disparos no WhatsApp sem taxa</p>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-zinc-300" />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleActionClick('enterprise', () => onNavigate('cofre_exames' as any), 'Cofre de Exames & DICOM', 'Guarda legal de 5 anos no Cloudflare R2.')}
                        className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 text-left transition-colors cursor-pointer group"
                      >
                        <div className="flex items-center gap-2.5">
                          <ShieldCheck className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-900" />
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-xs font-bold text-zinc-900">Cofre de exames & DICOM</h4>
                              {!isEnterpriseUnlocked && (
                                <span className="text-[9px] font-black text-indigo-700 bg-indigo-50 border border-indigo-200/80 px-1.5 py-0.2 rounded">Empresa</span>
                              )}
                            </div>
                            <p className="text-[10px] text-zinc-400">Guarda legal de 5 anos na nuvem R2</p>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-zinc-300" />
                      </button>
                    </>
                  )}
                </div>
              </div>

            </div>
          )}
        </div>

        {/* Card para Adicionar R-MAX à Tela Inicial (PWA) */}
        <InstallPrompt />
      </main>

      {/* ───────────────────────────────────────────────────────────────────────── */}
      {/* 7. MODAL "MINHA CONTA" (PERFIL, FONTE GRANDE, SUPORTE WHATSAPP E SAIR)    */}
      {/* ───────────────────────────────────────────────────────────────────────── */}
      {isAccountModalOpen && (
        <div className="fixed inset-0 z-50 bg-neutral-950/75 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200">
          <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 space-y-4 shadow-2xl border border-zinc-200/90 text-xs font-sans max-h-[92vh] overflow-y-auto">
            
            {/* Topo do Modal */}
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <h3 className="text-sm font-black text-zinc-900">Minha Conta</h3>
              <button
                type="button"
                onClick={() => setIsAccountModalOpen(false)}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-full hover:bg-zinc-100 transition-colors cursor-pointer"
                aria-label="Fechar minha conta"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Identidade do Negócio */}
            <div className="p-3.5 bg-zinc-50 border border-zinc-200/80 rounded-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-amber-500/10 text-amber-800 border border-amber-500/20 flex items-center justify-center font-black text-sm">
                  {getInitials(demoProfile.displayName || ownerFirstName)}
                </div>
                <div>
                  <h4 className="text-sm font-black text-zinc-900">{demoProfile.displayName || 'Minha Loja'}</h4>
                  <p className="text-xs text-zinc-500 font-medium">{effectiveNiche || 'Profissional R-MAX'}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsAccountModalOpen(false);
                  onNavigate('cadastro' as any);
                }}
                className="p-2 rounded-xl text-zinc-400 hover:text-zinc-900 hover:bg-zinc-200/60 transition-colors cursor-pointer"
                title="Editar dados da empresa"
              >
                <Edit3 className="h-4.5 w-4.5" />
              </button>
            </div>

            {/* Bloco do Plano */}
            <div className="space-y-1.5">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 px-1">
                Plano
              </span>
              <div className="p-3.5 bg-zinc-50 border border-zinc-200/80 rounded-2xl flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center">
                    <Crown className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <span className="text-xs font-black text-zinc-900 block">
                      {simulatedPlanTier === 'enterprise' ? 'Plano Empresa' : simulatedPlanTier === 'premium' ? 'Plano Especialista' : simulatedPlanTier === 'pro' ? 'Plano PRO' : 'Plano Gratuito'}
                    </span>
                    <span className="text-[10px] text-emerald-700 font-bold">
                      {isTrialActive ? 'Teste 14 Dias Ativo' : 'Ativo'}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsAccountModalOpen(false);
                    setIsSubscriptionModalOpen(true);
                  }}
                  className="text-xs text-amber-800 font-bold hover:underline cursor-pointer"
                >
                  Ver plano
                </button>
              </div>
            </div>

            {/* Bloco Deixar do Meu Jeito: Tamanho do Texto */}
            <div className="space-y-1.5">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 px-1">
                Deixar do Meu Jeito
              </span>
              <div className="p-3.5 bg-zinc-50 border border-zinc-200/80 rounded-2xl space-y-2">
                <span className="text-xs font-bold text-zinc-800 block">Tamanho do texto</span>
                <FontSizeSelector variant="segmented" />
              </div>
            </div>

            {/* Bloco Ajuda & Suporte no WhatsApp */}
            <div className="space-y-1.5">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 px-1">
                Ajuda
              </span>
              <div className="bg-zinc-50 border border-zinc-200/80 rounded-2xl divide-y divide-zinc-200/60 overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    setIsAccountModalOpen(false);
                    setIsHelpModalOpen(true);
                  }}
                  className="w-full p-3 flex items-center justify-between hover:bg-zinc-100/70 text-left transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-2.5">
                    <Compass className="h-4 w-4 text-zinc-500" />
                    <div>
                      <h5 className="text-xs font-bold text-zinc-900">Como funciona o R-MAX</h5>
                      <p className="text-[10px] text-zinc-400">Rever o passo a passo</p>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-zinc-300" />
                </button>

                <a
                  href="https://wa.me/5511920897453?text=Ol%C3%A1%2C%20preciso%20de%20ajuda%20com%20o%20meu%20R-MAX."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full p-3 flex items-center justify-between hover:bg-zinc-100/70 text-left transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-2.5">
                    <MessageCircle className="h-4 w-4 text-[#25D366]" />
                    <div>
                      <h5 className="text-xs font-bold text-zinc-900">Falar com o suporte</h5>
                      <p className="text-[10px] text-zinc-400">Resposta pelo WhatsApp</p>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-zinc-300" />
                </a>
              </div>
            </div>

            {/* Botão Sair da Conta */}
            {onLogout && (
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsAccountModalOpen(false);
                    onLogout();
                  }}
                  className="w-full py-2.5 text-xs font-bold text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-xl flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sair da conta</span>
                </button>
              </div>
            )}

          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────── */}
      {/* 8. MODAIS AUXILIARES DO SISTEMA                                           */}
      {/* ───────────────────────────────────────────────────────────────────────── */}
      
      {/* Menu Lateral Esquerdo Deslizante */}
      <ErpSidebarDrawer
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        displayName={demoProfile.displayName}
        logoUrl={demoProfile.logoUrl}
        tagline={demoProfile.tagline}
        nicheBadge={effectiveNiche}
        planTier={simulatedPlanTier}
        onNavigate={onNavigate}
        onOpenHelp={() => setIsHelpModalOpen(true)}
        onOpenFiscalConfig={() => setIsFiscalModalOpen(true)}
        onLogout={onLogout}
        canAccess={canAccess}
        handleActionClick={handleActionClick}
      />

      <HelpCenterModal 
        isOpen={isHelpModalOpen} 
        onClose={() => setIsHelpModalOpen(false)} 
        userNiche={effectiveNiche}
        userPlan={simulatedPlanTier}
      />

      <FiscalConfigModal
        isOpen={isFiscalModalOpen}
        onClose={() => setIsFiscalModalOpen(false)}
        defaultLogoUrl={demoProfile?.logoUrl}
      />

      {/* Modal de Detalhes da Assinatura */}
      {isSubscriptionModalOpen && (
        <div className="fixed inset-0 z-50 bg-neutral-950/75 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200">
          <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 space-y-4 shadow-2xl border border-zinc-200/90 text-xs font-sans max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-2xl flex items-center justify-center ${
                  simulatedPlanTier === 'premium' ? 'bg-amber-100 text-amber-800' : simulatedPlanTier === 'pro' ? 'bg-emerald-100 text-emerald-800' : 'bg-zinc-100 text-zinc-800'
                }`}>
                  {simulatedPlanTier === 'premium' && <Crown className="h-5 w-5 text-amber-600" />}
                  {simulatedPlanTier === 'pro' && <Zap className="h-5 w-5 text-emerald-600" />}
                  {simulatedPlanTier === 'free' && <ShieldCheck className="h-5 w-5 text-zinc-600" />}
                </div>
                <div>
                  <h3 className="text-sm font-black text-zinc-900 leading-tight">Minha Assinatura & Plano</h3>
                  <span className="text-[10px] text-zinc-500 font-medium">Informações de acesso e vencimento</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsSubscriptionModalOpen(false)}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-full hover:bg-zinc-100 transition-colors cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className={`p-4 rounded-2xl border ${
              simulatedPlanTier === 'premium'
                ? 'bg-amber-50/70 border-amber-200 text-amber-950'
                : simulatedPlanTier === 'pro'
                ? 'bg-emerald-50/70 border-emerald-200 text-emerald-950'
                : 'bg-zinc-50 border-zinc-200 text-zinc-900'
            } space-y-3`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] uppercase font-black tracking-widest text-zinc-500 block">
                    Plano Atual
                  </span>
                  <span className="text-base font-black">
                    {simulatedPlanTier === 'enterprise' ? 'Plano Empresa' : simulatedPlanTier === 'premium' ? 'Plano Especialista' : simulatedPlanTier === 'pro' ? 'Plano PRO' : 'Plano Gratuito'}
                  </span>
                </div>
                <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full bg-white/90 border border-zinc-200 shadow-2xs text-emerald-700 flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  <span>{isTrialActive ? 'Teste Ativo' : 'Ativo'}</span>
                </span>
              </div>

              <div className="pt-2.5 border-t border-zinc-200/60 flex items-start gap-2.5">
                <Calendar className="h-4 w-4 text-zinc-600 shrink-0 mt-0.5" />
                <div className="space-y-0.5 flex-1">
                  <span className="text-[10px] uppercase font-bold text-zinc-500 block">
                    {isTrialActive ? 'Validade do Teste Grátis' : 'Vencimento / Próxima Renovação'}
                  </span>
                  <p className="text-xs font-black text-zinc-900">
                    {formattedExpiry ? formattedExpiry.formatted : (
                      simulatedPlanTier === 'free' ? 'Plano Gratuito Permanente (5 orçamentos/mês)' : 'Renovação Automática Ativa'
                    )}
                  </p>
                  {formattedExpiry && formattedExpiry.daysRemaining !== null && (
                    <span className={`text-[10px] font-bold block ${
                      formattedExpiry.daysRemaining <= 5 ? 'text-amber-700' : 'text-emerald-700'
                    }`}>
                      {formattedExpiry.daysRemaining > 0
                        ? `(Restam ${formattedExpiry.daysRemaining} dias de acesso)`
                        : formattedExpiry.daysRemaining === 0
                        ? '⚠️ Vence hoje!'
                        : '⚠️ Acesso expirado'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-2 flex flex-col gap-2">
              {isPlayStore ? (
                <div className="p-3 bg-zinc-50 border border-zinc-200/90 rounded-2xl text-center space-y-1.5">
                  <p className="text-[11px] font-bold text-zinc-700">Informações da sua Conta</p>
                  <button
                    type="button"
                    onClick={() => setIsSubscriptionModalOpen(false)}
                    className="w-full mt-1.5 py-2.5 bg-zinc-900 hover:bg-black text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer active:scale-95"
                  >
                    Entendido / Fechar
                  </button>
                </div>
              ) : (
                <>
                  {onOpenPaywall && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsSubscriptionModalOpen(false);
                        onOpenPaywall(simulatedPlanTier === 'premium' ? 'specialist_annual' : 'annual');
                      }}
                      className="w-full py-2.5 px-4 bg-brand hover:bg-brand-dark text-white font-black rounded-xl text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all shadow-sm active:scale-95 cursor-pointer"
                    >
                      <Crown className="h-4 w-4" />
                      <span>{simulatedPlanTier === 'premium' ? 'Gerenciar / Renovar Plano' : 'Fazer Upgrade de Plano'}</span>
                    </button>
                  )}
                  <a
                    href="https://wa.me/5511920897453?text=Ol%C3%A1%2C%20tenho%20uma%20d%C3%BAvida%20sobre%20o%20meu%20plano%20no%20R-MAX."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-2.5 px-4 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 transition-all active:scale-95 text-center cursor-pointer border border-zinc-200/80"
                  >
                    <MessageCircle className="h-4 w-4 text-[#25D366]" />
                    <span>Dúvidas sobre o Plano? Falar no WhatsApp</span>
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Comando Rápido Agêntico (Text-to-Action) */}
      <AgenticCommandModal
        isOpen={isAgenticModalOpen}
        onClose={() => setIsAgenticModalOpen(false)}
        autoOpenCamera={agenticOpenCamera}
        autoStartVoice={agenticOpenVoice}
        userTier={
          simulatedPlanTier === 'enterprise'
            ? 'empresa'
            : simulatedPlanTier === 'premium'
            ? 'especialista'
            : simulatedPlanTier === 'pro'
            ? 'pro'
            : 'free'
        }
        onNavigate={onNavigate}
        onRequestUpgrade={(info) =>
          handleActionClick(info.tier, () => {}, info.feature, info.description)
        }
      />

    </div>
  );
}
